/**
 * The mesh configuration the backend actually accepts, and nothing else.
 *
 * Every field, bound and default below is transcribed from
 * `backend/flowlab/meshing/config.py`. That is the whole point of the module:
 * an inspector control that does not correspond to a field of
 * `MeshConfiguration` is a control whose value the platform would reject, and a
 * bound spelled loosely here turns a validation error into a mesh that silently
 * differs from what the panel showed. `FIELDS` is therefore the single
 * description the UI builds itself from -- there is no second list of knobs.
 *
 * Two fields a CAE user would expect are deliberately absent, because the
 * backend has no such concept: there is no maximum element size (sizing is
 * `target_element_size` with an optional floor) and there is no top-level
 * growth rate (growth is a property of a boundary-layer specification, where
 * the layers it grows are defined).
 *
 * `schema_version` is not a control. It is a constant the request carries so a
 * stored configuration can outlive the code that wrote it, and a user changing
 * it would be claiming to have written a different document.
 */

export const SCHEMA_VERSION = 1;

/** MeshingIntent -- the algorithm choice, expressed as intent (Test 7). */
export const INTENTS = [
  { value: 'robust', label: 'Robust', hint: 'Prefers completion over element quality' },
  { value: 'balanced', label: 'Balanced', hint: 'The default trade-off' },
  { value: 'quality', label: 'Quality', hint: 'Prefers element quality over speed' },
];

/**
 * The editable fields, in inspector order.
 *
 * `group` places a field in a module; `basic` fields are the ones §9 shows
 * before anything is expanded. `step` is a UI affordance only -- the backend
 * constrains the value, not its increment.
 */
export const FIELDS = [
  {
    key: 'target_element_size',
    label: 'Element size',
    group: 'sizing',
    basic: true,
    kind: 'length',
    min: 0,
    exclusiveMin: true,
    step: 0.1,
    required: true,
    hint: 'Target edge length, in model units',
  },
  {
    key: 'min_element_size',
    label: 'Minimum size',
    group: 'sizing',
    kind: 'length',
    min: 0,
    exclusiveMin: true,
    step: 0.1,
    nullable: true,
    hint: 'Floor for curvature and feature refinement. Unset means no floor.',
  },
  {
    key: 'dimension',
    label: 'Dimension',
    group: 'sizing',
    kind: 'choice',
    choices: [
      { value: 3, label: '3D' },
      { value: 2, label: '2D' },
    ],
    hint: 'Volume mesh, or surface mesh only',
  },
  {
    key: 'intent',
    label: 'Intent',
    group: 'algorithm',
    kind: 'choice',
    choices: INTENTS.map(({ value, label }) => ({ value, label })),
    hint: 'Mapped to a meshing algorithm by the mesher, never stored as one',
  },
  {
    key: 'element_order',
    label: 'Element order',
    group: 'algorithm',
    basic: true,
    kind: 'choice',
    choices: [
      { value: 1, label: '01 · Linear' },
      { value: 2, label: '02 · Quadratic' },
    ],
  },
  {
    key: 'seed',
    label: 'Random seed',
    group: 'algorithm',
    kind: 'integer',
    min: 0,
    step: 1,
    hint: 'Pinned so two runs of one configuration are byte-identical',
  },
  {
    key: 'curvature_resolution',
    label: 'Curvature',
    group: 'curvature',
    kind: 'integer',
    min: 0,
    max: 360,
    step: 2,
    unit: 'el/2π',
    hint: 'Elements per full turn. 0 disables curvature refinement.',
  },
  {
    key: 'feature_angle_degrees',
    label: 'Feature angle',
    group: 'curvature',
    kind: 'number',
    min: 0,
    max: 180,
    exclusiveMin: true,
    exclusiveMax: true,
    step: 1,
    unit: '°',
    hint: 'Dihedral angle above which an edge is preserved as a feature',
  },
  {
    key: 'cell_budget',
    label: 'Cell budget',
    group: 'quality',
    kind: 'integer',
    min: 0,
    exclusiveMin: true,
    step: 1000,
    nullable: true,
    unit: 'cells',
    hint: 'A run exceeding this is refused rather than truncated. Unset means no limit.',
  },
];

/** BoundaryLayerSpec -- edited per selected group, not globally. */
export const LAYER_FIELDS = [
  {
    key: 'first_layer_thickness',
    label: 'First layer',
    kind: 'length',
    min: 0,
    exclusiveMin: true,
    step: 0.01,
    required: true,
  },
  {
    key: 'layer_count',
    label: 'Layers',
    kind: 'integer',
    min: 1,
    max: 100,
    step: 1,
    required: true,
  },
  {
    key: 'growth_rate',
    label: 'Growth',
    kind: 'number',
    min: 1,
    max: 3,
    step: 0.05,
    required: true,
    hint: '1.00 is uniform layers',
  },
];

export const LAYER_DEFAULTS = Object.freeze({
  first_layer_thickness: 0.1,
  layer_count: 5,
  growth_rate: 1.2,
});

/**
 * A configuration with every field at the backend's own default.
 *
 * `target_element_size` has no default there -- it is required, because a
 * defaulted element size is how a mesh silently comes out at the wrong scale.
 * The caller supplies it, and until then the panel has nothing to generate
 * from, which is honest rather than inconvenient.
 */
export function defaultConfiguration(targetElementSize = null) {
  return {
    schema_version: SCHEMA_VERSION,
    dimension: 3,
    target_element_size: targetElementSize,
    min_element_size: null,
    curvature_resolution: 0,
    feature_angle_degrees: 40,
    element_order: 1,
    intent: 'balanced',
    boundary_layers: [],
    cell_budget: null,
    seed: 1,
  };
}

const FIELDS_BY_KEY = new Map(FIELDS.map((field) => [field.key, field]));
const LAYER_FIELDS_BY_KEY = new Map(LAYER_FIELDS.map((field) => [field.key, field]));

export function fieldFor(key) {
  return FIELDS_BY_KEY.get(key) || null;
}

/**
 * Whether `value` satisfies `field`'s bounds, and why not when it does not.
 *
 * The backend is authoritative and will refuse a bad value regardless; this
 * exists so the refusal is visible in the control the user is typing in rather
 * than arriving as a 422 after they press Generate.
 */
export function validateField(field, value) {
  if (value === null || value === undefined || value === '') {
    return field.required ? 'required' : null;
  }
  if (field.kind === 'choice') {
    return field.choices.some((choice) => choice.value === value) ? null : 'not a valid option';
  }
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return 'must be a number';
  if (field.kind === 'integer' && !Number.isInteger(number)) return 'must be a whole number';
  if (field.min !== undefined) {
    if (field.exclusiveMin ? number <= field.min : number < field.min) {
      return field.exclusiveMin ? `must be greater than ${field.min}` : `minimum ${field.min}`;
    }
  }
  if (field.max !== undefined) {
    if (field.exclusiveMax ? number >= field.max : number > field.max) {
      return field.exclusiveMax ? `must be less than ${field.max}` : `maximum ${field.max}`;
    }
  }
  return null;
}

/** Every problem in `configuration`, keyed by field. Empty means submittable. */
export function validateConfiguration(configuration) {
  const problems = {};
  for (const field of FIELDS) {
    const problem = validateField(field, configuration[field.key]);
    if (problem) problems[field.key] = problem;
  }
  (configuration.boundary_layers || []).forEach((spec, index) => {
    if (!Array.isArray(spec.groups) || spec.groups.length === 0) {
      problems[`boundary_layers.${index}.groups`] = 'needs at least one boundary group';
    }
    for (const field of LAYER_FIELDS) {
      const problem = validateField(field, spec[field.key]);
      if (problem) problems[`boundary_layers.${index}.${field.key}`] = problem;
    }
  });
  return problems;
}

/**
 * The configuration as the API takes it.
 *
 * Optional fields are sent as explicit nulls rather than omitted, because
 * `MeshConfiguration` declares them `| None` and a null is the value; and empty
 * strings from a cleared input become null rather than reaching validation as
 * `""`. Numbers are coerced here so a control that hands back a string cannot
 * produce a request whose type depends on how it was edited.
 */
export function toRequest(configuration) {
  const payload = { schema_version: SCHEMA_VERSION };
  for (const field of FIELDS) {
    const raw = configuration[field.key];
    if (raw === null || raw === undefined || raw === '') {
      payload[field.key] = null;
      continue;
    }
    payload[field.key] = field.kind === 'choice' ? raw : Number(raw);
  }
  payload.boundary_layers = (configuration.boundary_layers || []).map((spec) => {
    const layer = { groups: [...spec.groups] };
    for (const field of LAYER_FIELDS) layer[field.key] = Number(spec[field.key]);
    return layer;
  });
  return payload;
}

/**
 * A stored configuration, normalised for editing.
 *
 * A Generation's configuration is the record of what ran, so re-generating from
 * one starts from exactly those values. Unknown keys are dropped rather than
 * carried: the request model forbids extras, so passing an unrecognised field
 * back would turn a stale document into a rejected request.
 */
export function fromStored(stored) {
  const base = defaultConfiguration();
  if (!stored || typeof stored !== 'object') return base;
  for (const field of FIELDS) {
    if (Object.hasOwn(stored, field.key)) base[field.key] = stored[field.key];
  }
  base.boundary_layers = (stored.boundary_layers || []).map((spec) => {
    const layer = { groups: [...(spec.groups || [])] };
    for (const field of LAYER_FIELDS) {
      layer[field.key] = Object.hasOwn(spec, field.key)
        ? spec[field.key]
        : LAYER_DEFAULTS[field.key];
    }
    return layer;
  });
  return base;
}

export function layerFieldFor(key) {
  return LAYER_FIELDS_BY_KEY.get(key) || null;
}
